/* ==========================================================================
   PROMO SUITE
   Countdown, click-to-copy discount codes, and the promo popup.
   Self-contained: no theme.js internals are relied on, so a theme update
   will not break any of this.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     <promo-countdown data-ends="1785801540">
     data-ends is a UNIX timestamp in SECONDS, already resolved to real UTC
     by promo.liquid, so the browser's own timezone never enters into it.
     ------------------------------------------------------------------------ */
  class PromoCountdown extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-promo-countdown-value]');
      this.endsAt = parseInt(this.dataset.ends, 10) * 1000;

      if (!this.output || !this.endsAt || isNaN(this.endsAt)) {
        this.hidden = true;
        return;
      }

      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = this.endsAt - Date.now();

      if (remaining <= 0) {
        clearInterval(this.timer);
        this.expire();
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Over a day out, seconds are noise. Under a day, they create urgency.
      this.output.textContent =
        days > 0
          ? days + 'd ' + pad(hours) + 'h ' + pad(minutes) + 'm'
          : pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
    }

    // The promotion has run out while the page was open. Take the whole
    // callout down rather than leaving a dead offer on screen — this also
    // covers pages served from Shopify's cache a little after the end time.
    expire() {
      const root = this.closest('.promo');
      if (root) {
        root.setAttribute('data-promo-expired', '');
      } else {
        this.hidden = true;
      }
      document.dispatchEvent(new CustomEvent('promo:expired'));
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  if (!customElements.get('promo-countdown')) {
    customElements.define('promo-countdown', PromoCountdown);
  }

  /* ------------------------------------------------------------------------
     Click-to-copy discount codes.
     Delegated, so codes rendered later (ajax cart, quick view) work too.
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    const button = event.target.closest('button.promo__code');
    if (!button) return;

    event.preventDefault();
    const code = button.dataset.promoCode;
    if (!code) return;

    copyText(code).then(function (ok) {
      if (!ok) return;
      button.setAttribute('data-copied', '');
      clearTimeout(button._promoResetTimer);
      button._promoResetTimer = setTimeout(function () {
        button.removeAttribute('data-copied');
      }, 2000);
    });
  });

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(
        function () { return true; },
        function () { return legacyCopy(text); }
      );
    }
    return Promise.resolve(legacyCopy(text));
  }

  function legacyCopy(text) {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:-9999px;opacity:0;';
    document.body.appendChild(field);
    field.select();

    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (e) {
      ok = false;
    }

    document.body.removeChild(field);
    return ok;
  }

  /* ------------------------------------------------------------------------
     Promo popup
     ------------------------------------------------------------------------ */
  function initPopup(root) {
    if (!root || root._promoInit) return;
    root._promoInit = true;

    const dialog = root.querySelector('.promo-popup__dialog');
    const testMode = root.dataset.testMode === 'true';
    const delay = parseInt(root.dataset.delaySeconds, 10) || 0;
    const frequencyDays = parseInt(root.dataset.frequencyDays, 10);
    // Key the frequency cap to the campaign itself, so dismissing one
    // promotion does not silence the next one.
    const handleEl = root.querySelector('[data-promo-handle]');
    const campaign = (handleEl && handleEl.dataset.promoHandle) || 'default';
    const storageKey = 'promo_popup_' + campaign;
    const triggerOnExit = root.dataset.exitIntent === 'true';

    let lastFocused = null;
    let opened = false;

    // In the theme editor the popup should always be reachable.
    if (!testMode && isSuppressed()) return;

    function isSuppressed() {
      try {
        const until = parseInt(window.localStorage.getItem(storageKey), 10);
        return !!until && Date.now() < until;
      } catch (e) {
        return false; // private browsing etc — just show it
      }
    }

    function suppress() {
      if (testMode) return;
      try {
        const days = isNaN(frequencyDays) ? 7 : frequencyDays;
        window.localStorage.setItem(storageKey, Date.now() + days * 86400000);
      } catch (e) {
        /* storage unavailable — the popup simply shows again next visit */
      }
    }

    function open() {
      if (opened) return;
      opened = true;
      lastFocused = document.activeElement;
      root.setAttribute('data-open', '');
      root.removeAttribute('aria-hidden');
      if (dialog) dialog.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      opened = false;
      root.removeAttribute('data-open');
      root.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', onKeydown);
      suppress();
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }

    root.querySelectorAll('[data-promo-popup-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    // Following the CTA counts as engagement — don't nag on the next visit.
    const cta = root.querySelector('.promo__popup-cta');
    if (cta) cta.addEventListener('click', suppress);

    // Copying the code is the whole point of the popup; close once they have it.
    root.addEventListener('click', function (event) {
      if (event.target.closest('button.promo__code')) {
        setTimeout(close, 1200);
      }
    });

    // An expired promo should take the popup with it.
    document.addEventListener('promo:expired', function () {
      if (opened) close();
      root.remove();
    });

    if (triggerOnExit) {
      document.addEventListener('mouseout', function onExit(event) {
        if (event.clientY <= 0 && !event.relatedTarget) {
          document.removeEventListener('mouseout', onExit);
          open();
        }
      });
    }

    setTimeout(open, delay * 1000);
  }

  function initAll() {
    document.querySelectorAll('.promo-popup').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    const popup = event.target.querySelector('.promo-popup');
    if (popup) initPopup(popup);
  });
})();
